/**
 * negoMeta.js — Constantes & helpers du Centre de Négociations (cockpit)
 * Charte Baobab : vert #16A34A, statuts colorés (orange SLA, rouge urgent,
 * bleu discussion, violet escalade, vert acceptation).
 */
import {
  DollarSign, Calendar, CreditCard, Truck, MapPin, XCircle, MessageSquare,
} from 'lucide-react';

export const GREEN = '#16A34A';
export const TERMINAL_STATUSES = ['accepted', 'rejected', 'expired', 'cancelled'];

/* ---------- Formatters ---------- */
export const fmtXof = (v) => (v == null || isNaN(v))
  ? '—' : `${Math.round(v).toLocaleString('fr-FR')} XOF`;

export const fmtXofCompact = (v) => {
  if (v == null || isNaN(v)) return '—';
  if (Math.abs(v) >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)} Md`;
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M`;
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1_000)} k`;
  return Math.round(v).toString();
};

export const fmtRelative = (dateStr) => {
  if (!dateStr) return '—';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `Il y a ${m} min`;
  if (m < 1440) return `Il y a ${Math.floor(m / 60)} h`;
  const d = Math.floor(m / 1440);
  if (d < 7) return `Il y a ${d} j`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

export const fmtMinutes = (mins) => {
  if (mins == null) return '—';
  if (mins < 60) return `${Math.round(mins)}min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m === 0 ? `${h}h` : `${h}h${m.toString().padStart(2, '0')}m`;
};

export const fmtDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/* ---------- Statuts (libellés + couleurs, calqués sur la maquette) ---------- */
export const STATUS_META = {
  open:             { label: 'Nouvelle demande',  cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  pending_internal: { label: 'En discussion',     cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  countered:        { label: 'Contre-offre reçue', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  pending_client:   { label: 'Offre envoyée',     cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  escalated:        { label: 'Validation requise', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  accepted:         { label: 'Acceptée',          cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:         { label: 'Refusée',           cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  expired:          { label: 'Expirée',           cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  cancelled:        { label: 'Annulée',           cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

/* ---------- Types de négociation ---------- */
export const TYPE_META = {
  price_change:      { label: 'Modification prix',   icon: DollarSign,   color: 'text-emerald-600', bg: 'bg-emerald-50' },
  schedule_change:   { label: 'Changement date',     icon: Calendar,     color: 'text-blue-600',    bg: 'bg-blue-50' },
  payment_terms:     { label: 'Conditions paiement', icon: CreditCard,   color: 'text-indigo-600',  bg: 'bg-indigo-50' },
  truck_type_change: { label: 'Type véhicule',       icon: Truck,        color: 'text-orange-600',  bg: 'bg-orange-50' },
  route_change:      { label: 'Changement trajet',   icon: MapPin,       color: 'text-violet-600',  bg: 'bg-violet-50' },
  cancel_or_pause:   { label: 'Annulation/Pause',    icon: XCircle,      color: 'text-rose-600',    bg: 'bg-rose-50' },
  other:             { label: 'Autre',               icon: MessageSquare, color: 'text-slate-600',  bg: 'bg-slate-100' },
};

/* ---------- SLA helpers ---------- */
export const slaInfo = (nego) => {
  const deadline = nego?.slaDeadlineAt ? new Date(nego.slaDeadlineAt).getTime() : null;
  const terminal = TERMINAL_STATUSES.includes(nego?.status);
  if (!deadline || terminal) return { remainingMin: null, overdue: false, pct: 0, label: '—', cls: 'text-slate-400' };
  const remainingMs = deadline - Date.now();
  const overdue = remainingMs < 0;
  const remainingMin = Math.floor(Math.abs(remainingMs) / 60000);
  const human = remainingMin < 60
    ? `${remainingMin} min`
    : remainingMin < 1440
      ? `${Math.floor(remainingMin / 60)}h ${remainingMin % 60 ? `${remainingMin % 60}m` : ''}`.trim()
      : `${Math.floor(remainingMin / 1440)}j`;
  const label = overdue ? `Dépassé +${human}` : `${human} restantes`;
  const cls = overdue ? 'text-rose-600 font-semibold'
    : remainingMin < 60 ? 'text-orange-600 font-medium'
    : 'text-emerald-600';
  return { remainingMin: overdue ? -remainingMin : remainingMin, overdue, label, cls };
};

/* progression SLA écoulée (0→100) pour la barre du drawer */
export const slaProgress = (nego) => {
  const created = nego?.createdAt ? new Date(nego.createdAt).getTime() : null;
  const deadline = nego?.slaDeadlineAt ? new Date(nego.slaDeadlineAt).getTime() : null;
  if (!created || !deadline || deadline <= created) return { pct: 0, overdue: false };
  const elapsed = Date.now() - created;
  const total = deadline - created;
  const pct = Math.min(100, Math.max(0, (elapsed / total) * 100));
  return { pct, overdue: Date.now() > deadline };
};

/* ---------- Priorité dérivée ---------- */
export const PRIORITY_META = {
  urgent:    { label: 'Urgent',       cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  sla_risk:  { label: 'SLA à risque', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  escalated: { label: 'Escaladée',    cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  normal:    { label: 'Normal',       cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export const derivePriority = (nego) => {
  if (nego?.status === 'escalated') return PRIORITY_META.escalated;
  const s = slaInfo(nego);
  if (s.overdue) return PRIORITY_META.urgent;
  if (s.remainingMin != null && s.remainingMin < 60) return PRIORITY_META.sla_risk;
  return PRIORITY_META.normal;
};

/* ---------- Offres ---------- */
export const initialOffer = (nego) => nego?.currentOffer?.previousPrice ?? nego?.operationCurrentPrice ?? null;
export const negotiatedOffer = (nego) => nego?.currentOffer?.proposedPrice ?? null;
export const offerGap = (nego) => {
  const init = initialOffer(nego);
  const neg = negotiatedOffer(nego);
  if (init == null || neg == null) return null;
  return neg - init;
};
